/**
 * Release drivers whose contract just completed — pure helper extracted
 * from coreSlice.nextDay (02/06/2026 night refactor).
 *
 * Previously, when a contract finished its `daysRemaining` cycle the
 * vehicle was released but the driver kept `status: 'active'` plus a
 * stale `assignedVehicleId`. The daily fatigue tick then treated them
 * as still working and prevented recovery during weekdays — a
 * gameplay-breaking bug reported on 02/06/2026.
 *
 * 06/02/2026 — P1 retour user : un chauffeur revenant d'une mission
 * ponctuelle long-courrier (statut véhicule 'returning' pour 1 j) ne
 * doit PAS être immédiatement re-assignable. Avant, le chauffeur passait
 * `idle` instantanément tandis que son camion restait bloqué en retour
 * — état incohérent, le joueur tentait d'enchaîner et l'UI refusait
 * sans explication claire. On aligne désormais le `returnUntilDay` du
 * chauffeur sur celui du camion.
 *
 * Pure : no side effects.
 */

/**
 * @param {Array}  drivers - drivers after the fatigue/satisfaction tick
 * @param {Array}  completedContractIds - instanceIds of contracts that
 *   finished this tick
 * @param {Array}  vehicles - the BEFORE-update vehicles array (the one
 *   that still has `assignedContractId` pointing at the completed
 *   contracts ; we use it to map contractId → vehicleId)
 * @param {Object} [opts]
 * @param {Array<string>} [opts.completedPunctualIds] - subset of
 *   completedContractIds that were punctual missions (require 1-day return).
 * @param {Array}  [opts.updatedVehicles] - post-tick vehicles array
 *   carrying the `returningUntilDay` we want to mirror on drivers.
 * @returns {Array} the new drivers array
 */
export function releaseDriversOnContractComplete(drivers, completedContractIds, vehicles, opts = {}) {
  if (!completedContractIds || completedContractIds.length === 0) return drivers;
  const punctualSet = new Set(opts.completedPunctualIds || []);
  const completedVehicleIds = new Set(
    (vehicles || [])
      .filter(v => completedContractIds.includes(v.assignedContractId))
      .map(v => v.id),
  );
  if (completedVehicleIds.size === 0) return drivers;
  // Build vehicleId → punctual? + returningUntilDay map from the BEFORE
  // and AFTER vehicle arrays so we can mirror the 'returning' delay onto
  // the driver record.
  const punctualVehicleIds = new Set(
    (vehicles || [])
      .filter(v => punctualSet.has(v.assignedContractId))
      .map(v => v.id),
  );
  const returningUntilByVehicleId = {};
  (opts.updatedVehicles || []).forEach(v => {
    if (punctualVehicleIds.has(v.id) && Number.isFinite(v.returningUntilDay)) {
      returningUntilByVehicleId[v.id] = v.returningUntilDay;
    }
  });
  return drivers.map(d => {
    if (!d.assignedVehicleId || !completedVehicleIds.has(d.assignedVehicleId)) return d;
    const isPunctual = punctualVehicleIds.has(d.assignedVehicleId);
    const returningUntilDay = returningUntilByVehicleId[d.assignedVehicleId];
    if (isPunctual && Number.isFinite(returningUntilDay)) {
      // Mirror the truck's 1-day-return on the driver — they are travelling
      // back with the empty truck, so they cannot be re-assigned today.
      return {
        ...d,
        assignedVehicleId: null,
        status: 'returning',
        returningUntilDay,
      };
    }
    return { ...d, assignedVehicleId: null, status: 'idle' };
  });
}
